import React from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import Immutable from 'immutable';

import AddressOverview from '../../../../../components/AddressOverview';
import { getUsableAddresses } from '../../../../../components/AddressOverview/getUsableAddresses';
import InformationOverlay from '../../../../../components/InformationOverlay';

/**
 * Address Selection Overlay Component
 *
 * Provides an interface for users to select delivery addresses from their address
 * book. Shows an overlay with available addresses and allows selection.
 *
 * @component
 */

interface AddressSelectOverlayProps {
  /** Callback to update the selected address index */
  setSelectedAddressIndex: (index: number) => void;
  /** React ref to the checkout form */
  // TODO: Replace 'any' with proper Formik ref type when available
  formRef: React.RefObject<any>;
  /** Currently selected address index, or null if none selected */
  selectedAddressIndex: number | null;
  /** Callback to populate form with selected address */
  // TODO: Replace 'any' with proper address type definition
  selectAddress: (ref: React.RefObject<any>, address: any) => void;
}

const AddressSelectOverlay: React.FC<AddressSelectOverlayProps> = ({
  setSelectedAddressIndex,
  formRef,
  selectedAddressIndex,
  selectAddress,
}) => {
  const { t } = useTranslation();
  const uid = useSelector((state: any) => state.getIn(['login', 'uid'], null));
  const addressesRaw = useSelector((state: any) => state.getIn(['user', uid, 'profile', 'address'], Immutable.List()));
  const { usableAddresses: usableProfileAddresses } =
    getUsableAddresses(addressesRaw);
  const addressSelected = selectedAddressIndex !== null;

  return (
    <InformationOverlay
      buttonClassName={`Button--text ${
        addressSelected ? 'Button--edit' : 'Button--add Button--inverted'
      }`}
      overlayKey="checkout_address_overview"
      buttonIcon={addressSelected ? 'edit' : 'plus'}
      buttonTitle={
        addressSelected
          ? t('address.change_address')
          : t('address.choose_from_addressbook')
      }
      disableClosers
    >
      <AddressOverview
        selectionMode
        title={
          usableProfileAddresses.size === 0
            ? t('address.first_delivery_address')
            : t('address.choose_delivery_address')
        }
        activeIndex={Number(selectedAddressIndex)}
        onSelect={(index: number) => {
          const formSelectedAddress = addressesRaw.get(index);
          setSelectedAddressIndex(index);
          selectAddress(formRef, formSelectedAddress);
        }}
      />
    </InformationOverlay>
  );
};

export default AddressSelectOverlay;
